import { supabase } from '../lib/supabase';
import { triggerPropertyAlertNotifications } from './propertyAlertsService';

// Get all properties with enhanced error handling
export const getAllProperties = async () => {
  try {
    const { data, error } = await supabase?.from('properties')?.select(`
        *,
        commune:communes(id, name, district, description, average_price),
        agent:user_profiles!agent_id(id, full_name, email, phone),
        owner:user_profiles!owner_id(id, full_name, email, phone)
      `)?.eq('status', 'available')?.order('created_at', { ascending: false });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch properties' };
  }
};

// Get property by ID
export const getPropertyById = async (id) => {
  try {
    const { data, error } = await supabase?.from('properties')?.select(`
        *,
        commune:communes(id, name, district, description, average_price, amenities),
        agent:user_profiles!agent_id(id, full_name, email, phone),
        owner:user_profiles!owner_id(id, full_name, email, phone)
      `)?.eq('id', id)?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to fetch property' };
  }
};

// Get property by ID (alias for getPropertyById for compatibility)
export const getProperty = async (id) => {
  return await getPropertyById(id);
};

// Search properties with filters
export const searchProperties = async (filters = {}) => {
  try {
    let query = supabase?.from('properties')?.select(`
        *,
        commune:communes(id, name, district, description, average_price),
        agent:user_profiles!agent_id(id, full_name, email, phone)
      `);

    // Apply filters
    if (filters?.commune_id) {
      query = query?.eq('commune_id', filters?.commune_id);
    }
    if (filters?.property_type) {
      query = query?.eq('property_type', filters?.property_type);
    }
    if (filters?.market_type) {
      query = query?.eq('market_type', filters?.market_type);
    }
    if (filters?.min_price) {
      query = query?.gte('price', filters?.min_price);
    }
    if (filters?.max_price) {
      query = query?.lte('price', filters?.max_price);
    }
    if (filters?.bedrooms) {
      query = query?.gte('bedrooms', filters?.bedrooms);
    }
    if (filters?.bathrooms) {
      query = query?.gte('bathrooms', filters?.bathrooms);
    }

    // Only show available properties by default
    query = query?.eq('status', 'available');

    const { data, error } = await query?.order('created_at', { ascending: false });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to search properties' };
  }
};

// Create new property (admin/agent only)
export const createProperty = async (propertyData) => {
  try {
    const dataToInsert = {
      ...propertyData,
    };

    if (dataToInsert?.owner_id === undefined) {
      delete dataToInsert?.owner_id;
    }
    if (dataToInsert?.agent_id === undefined) {
      delete dataToInsert?.agent_id;
    }

    const { data, error } = await supabase?.from('properties')?.insert([dataToInsert])?.select()?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    // Silently trigger property alert notifications for matching saved alerts
    if (data?.id) {
      triggerPropertyAlertNotifications(data?.id);
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to create property' };
  }
};

// Update property (admin/agent/owner only)
export const updateProperty = async (id, updates) => {
  try {
    const { data, error } = await supabase?.from('properties')?.update(updates)?.eq('id', id)?.select()?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to update property' };
  }
};

// Delete property (admin/owner only)
export const deleteProperty = async (id) => {
  try {
    const { error } = await supabase?.from('properties')?.delete()?.eq('id', id);

    if (error) {
      return { error: error?.message };
    }

    return { error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { error: 'Failed to delete property' };
  }
};

// Get properties by commune
export const getPropertiesByCommune = async (communeId) => {
  try {
    const { data, error } = await supabase?.from('properties')?.select(`
        *,
        commune:communes(id, name, district, description, average_price),
        agent:user_profiles!agent_id(id, full_name, email, phone)
      `)?.eq('commune_id', communeId)?.eq('status', 'available')?.order('created_at', { ascending: false });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch properties by commune' };
  }
};

// Get related properties based on commune and property type
export const getRelatedProperties = async (excludeId, communeId, propertyType, limit = 6) => {
  try {
    const { data, error } = await supabase?.from('properties')?.select(`
        *,
        commune:communes(id, name, district, description, average_price),
        agent:user_profiles!agent_id(id, full_name, email, phone)
      `)?.eq('commune_id', communeId)?.eq('property_type', propertyType)?.neq('id', excludeId)?.eq('status', 'available')?.order('created_at', { ascending: false })?.limit(limit);

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch related properties' };
  }
};

// Submit property inquiry - handles both authenticated and anonymous submissions
export const submitPropertyInquiry = async (inquiryData) => {
  try {
    const { data, error } = await supabase
      ?.from('property_inquiries')
      ?.insert([{
        property_id: inquiryData?.property_id,
        client_id: inquiryData?.client_id || null, // Can be null for anonymous submissions
        message: inquiryData?.message,
        client_phone: inquiryData?.client_phone,
        preferred_contact_time: inquiryData?.preferred_contact_time,
        status: inquiryData?.status || 'new'
      }])
      ?.select()
      ?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to submit property inquiry' };
  }
};

// Get property inquiries for admin/agent access
export const getPropertyInquiries = async (filters = {}) => {
  try {
    let query = supabase
      ?.from('property_inquiries')
      ?.select(`
        *,
        property:properties(id, title, property_type, commune:communes(name)),
        client:user_profiles(id, full_name, email, phone)
      `);

    // Apply filters
    if (filters?.property_id) {
      query = query?.eq('property_id', filters?.property_id);
    }
    if (filters?.status) {
      query = query?.eq('status', filters?.status);
    }
    if (filters?.client_id) {
      query = query?.eq('client_id', filters?.client_id);
    }

    const { data, error } = await query?.order('created_at', { ascending: false });

    if (error) {
      return { data: [], error: error?.message };
    }

    return { data: data || [], error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch property inquiries' };
  }
};

// Update property inquiry status (for admin/agent responses)
export const updatePropertyInquiry = async (id, updates) => {
  try {
    const { data, error } = await supabase
      ?.from('property_inquiries')
      ?.update(updates)
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to update property inquiry' };
  }
};

// Get property statistics
export const getPropertyStats = async () => {
  try {
    const { data: properties, error: propertyError } = await supabase
      ?.from('properties')
      ?.select('id, price, property_type, market_type, status');

    if (propertyError) {
      return { data: null, error: propertyError?.message };
    }

    const totalProperties = properties?.length || 0;
    const availableProperties = properties?.filter(p => p?.status === 'available')?.length || 0;
    const soldProperties = properties?.filter(p => p?.status === 'sold')?.length || 0;
    
    const prices = properties?.map(p => p?.price)?.filter(Boolean);
    const averagePrice = prices?.length > 0 ? Math.round(prices?.reduce((sum, price) => sum + price, 0) / prices?.length) : 0;

    const stats = {
      total_properties: totalProperties,
      available_properties: availableProperties,
      sold_properties: soldProperties,
      average_price: averagePrice,
      property_types: [...new Set(properties?.map(p => p?.property_type)?.filter(Boolean))]?.length || 0
    };

    return { data: stats, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to fetch property statistics' };
  }
};

// Add video to property
export const addPropertyVideo = async (propertyId, videoData) => {
  try {
    // First, get the current property to update the videos array
    const { data: property, error: fetchError } = await supabase
      ?.from('properties')
      ?.select('videos')
      ?.eq('id', propertyId)
      ?.single();

    if (fetchError) {
      return { data: null, error: fetchError?.message };
    }

    const currentVideos = property?.videos || [];
    const newVideo = {
      id: Date.now()?.toString(), // Simple ID for video
      url: videoData?.url,
      title: videoData?.title || 'Vidéo de propriété',
      description: videoData?.description || '',
      duration: videoData?.duration || null,
      thumbnail: videoData?.thumbnail || null,
      added_at: new Date()?.toISOString()
    };

    const updatedVideos = [...currentVideos, newVideo];

    const { data, error } = await supabase
      ?.from('properties')
      ?.update({ videos: updatedVideos })
      ?.eq('id', propertyId)
      ?.select()
      ?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to add video to property' };
  }
};

// Update property video
export const updatePropertyVideo = async (propertyId, videoId, videoData) => {
  try {
    // Get current property videos
    const { data: property, error: fetchError } = await supabase
      ?.from('properties')
      ?.select('videos')
      ?.eq('id', propertyId)
      ?.single();

    if (fetchError) {
      return { data: null, error: fetchError?.message };
    }

    const currentVideos = property?.videos || [];
    const updatedVideos = currentVideos?.map(video => 
      video?.id === videoId 
        ? { ...video, ...videoData, updated_at: new Date()?.toISOString() }
        : video
    );

    const { data, error } = await supabase
      ?.from('properties')
      ?.update({ videos: updatedVideos })
      ?.eq('id', propertyId)
      ?.select()
      ?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to update property video' };
  }
};

// Remove video from property
export const removePropertyVideo = async (propertyId, videoId) => {
  try {
    // Get current property videos
    const { data: property, error: fetchError } = await supabase
      ?.from('properties')
      ?.select('videos')
      ?.eq('id', propertyId)
      ?.single();

    if (fetchError) {
      return { data: null, error: fetchError?.message };
    }

    const currentVideos = property?.videos || [];
    const updatedVideos = currentVideos?.filter(video => video?.id !== videoId);

    const { data, error } = await supabase
      ?.from('properties')
      ?.update({ videos: updatedVideos })
      ?.eq('id', propertyId)
      ?.select()
      ?.single();

    if (error) {
      return { data: null, error: error?.message };
    }

    return { data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to remove video from property' };
  }
};

// Bulk update property videos with enhanced JSON coercion fix
export const updatePropertyVideos = async (propertyId, videosArray) => {
  try {
    // Enhanced validation to prevent JSON coercion issues
    let validatedVideos = [];
    
    if (videosArray === null || videosArray === undefined) {
      validatedVideos = [];
    } else if (Array.isArray(videosArray)) {
      validatedVideos = videosArray?.map((video, index) => {
        // Handle both object and string formats
        if (typeof video === 'string') {
          return {
            id: `video_${Date.now()}_${index}`,
            url: video,
            title: `Vidéo ${index + 1}`,
            description: '',
            duration: null,
            thumbnail: null,
            added_at: new Date()?.toISOString()
          };
        } else if (video && typeof video === 'object') {
          return {
            id: video?.id || `video_${Date.now()}_${index}`,
            url: video?.url || '',
            title: video?.title || `Vidéo ${index + 1}`,
            description: video?.description || '',
            duration: video?.duration || null,
            thumbnail: video?.thumbnail || null,
            added_at: video?.added_at || new Date()?.toISOString()
          };
        }
        return null;
      })?.filter(Boolean); // Remove null entries
    } else if (typeof videosArray === 'object') {
      // Single video object - convert to array
      validatedVideos = [{
        id: videosArray?.id || `video_${Date.now()}`,
        url: videosArray?.url || '',
        title: videosArray?.title || 'Vidéo 1',
        description: videosArray?.description || '',
        duration: videosArray?.duration || null,
        thumbnail: videosArray?.thumbnail || null,
        added_at: videosArray?.added_at || new Date()?.toISOString()
      }];
    }

    // Use the safe update function to prevent coercion errors
    const { data, error } = await supabase
      ?.rpc('safe_update_property_videos', {
        property_id: propertyId,
        new_videos: validatedVideos
      });

    if (error) {
      // Fallback to direct update if RPC fails
      console.warn('RPC failed, trying direct update:', error?.message);
      
      const { data: fallbackData, error: fallbackError } = await supabase
        ?.from('properties')
        ?.update({ videos: validatedVideos })
        ?.eq('id', propertyId)
        ?.select()
        ?.single();

      if (fallbackError) {
        return { data: null, error: fallbackError?.message };
      }

      return { data: fallbackData, error: null };
    }

    // Check if RPC returned error in data
    if (data?.error) {
      return { data: null, error: data?.error };
    }

    return { data: data?.property || data, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: null, 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: null, error: 'Failed to update property videos' };
  }
};

// Enhanced function to safely get property videos
export const getPropertyVideos = async (propertyId) => {
  try {
    const { data, error } = await supabase
      ?.from('properties')
      ?.select('videos')
      ?.eq('id', propertyId)
      ?.single();

    if (error) {
      return { data: [], error: error?.message };
    }

    // Ensure videos is always an array
    const videos = Array.isArray(data?.videos) ? data?.videos : [];
    return { data: videos, error: null };
  } catch (error) {
    if (error?.message?.includes('Failed to fetch')) {
      return { 
        data: [], 
        error: 'Cannot connect to database. Your Supabase project may be paused or inactive.' 
      };
    }
    return { data: [], error: 'Failed to fetch property videos' };
  }
};

// Export service object for named destructuring import
export const propertyService = {
  getAllProperties,
  getPropertyById,
  getProperty,
  getRelatedProperties,
  searchProperties,
  createProperty,
  updateProperty,
  deleteProperty,
  getPropertiesByCommune,
  submitPropertyInquiry,
  getPropertyInquiries,
  updatePropertyInquiry,
  getPropertyStats,
  addPropertyVideo,
  updatePropertyVideo,
  removePropertyVideo,
  updatePropertyVideos,
  getPropertyVideos
};

// Export as default for default import
export default {
  getAllProperties,
  getPropertyById,
  getProperty,
  getRelatedProperties,
  searchProperties,
  createProperty,
  updateProperty,
  deleteProperty,
  getPropertiesByCommune,
  submitPropertyInquiry,
  getPropertyInquiries,
  updatePropertyInquiry,
  getPropertyStats,
  addPropertyVideo,
  updatePropertyVideo,
  removePropertyVideo,
  updatePropertyVideos,
  getPropertyVideos
};

function communeService(...args) {
  // eslint-disable-next-line no-console
  console.warn('Placeholder: communeService is not implemented yet.', args);
  return null;
}

export { communeService };